/**
 * SettingsPage — 系统设置中心（Issue #227 + Issue #243 + Issue #323）。
 *
 * 真相边界：
 *   - 当前会话信息从 AuthContext 读取（来自 `/api/v1/auth/me`）：
 *     email、userId、tenantId、issuedAt、来源、模块数、权限数。tenantId 仅展示其
 *     掩码后片段，避免在普通管理员界面铺开租户 ID 全量；权限列表默认折叠展示数量，
 *     避免无意义地泄露权限矩阵。
 *   - 仅展示当前版本已落地的设置类模块的真实导航入口（AI 模型中心、短信模板/日志），
 *     并按权限过滤。无权限模块不会出现。
 *   - 角色与权限矩阵管理、租户/机构、参数中心、会话空闲超时、微信绑定、增强验证、
 *     审计搜索等已落地的设置 section 不再被同时挂载在一张长页里；Issue #323 起
 *     改为按模块切换的「设置选项卡」结构：仅当前选中的可见 section 才会被挂载，
 *     这样隐藏/未授权的 section 不会触发其 hook 的 API 请求。
 *   - 退出登录直接复用 AuthContext.signOut（已与 `/api/v1/auth/logout` 对接）。
 *   - 不展示 access token、refresh token、cookie 值、生产环境变量或任何凭据。
 */
import {
  useMemo,
  useState,
  type ComponentType,
  type ReactElement,
} from 'react';
import { Link } from 'wouter';
import { Bot, LogOut, MessageSquare, ShieldCheck, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { canSeeAiModelCenterMenu } from '@/lib/aiModelCenterPermissions';
import { canSeeSmsAdminMenu } from '@/lib/smsPermissions';
import SettingsRbacSection from './SettingsRbacSection';
import SystemParametersSection from './SystemParametersSection';
import TenantOrganizationSection, {
  canSeeTenantOrganizationSection,
} from './TenantOrganizationSection';
import WechatBindingManagementSection from './WechatBindingManagementSection';
import EnhancedVerificationPolicyCenterSection from './EnhancedVerificationPolicyCenterSection';
import AuditLogSearchSection from './AuditLogSearchSection';
import SessionIdlePolicySection from './SessionIdlePolicySection';
import { canReadWechatBindingManagement } from '@/lib/wechatBindingPermissions';
import { canReadEnhancedVerification } from '@/lib/enhancedVerificationPermissions';
import { canReadAuditLogs } from '@/lib/auditLogPermissions';
import { canReadSessionPolicy } from '@/lib/sessionPolicyPermissions';

interface SettingsLinkSpec {
  readonly to: string;
  readonly label: string;
  readonly description: string;
  readonly icon: ComponentType<{ className?: string }>;
  readonly visible: (permissions: readonly string[]) => boolean;
}

const SETTINGS_LINKS: readonly SettingsLinkSpec[] = [
  {
    to: '/ai-model-center',
    label: 'AI 模型中心',
    description:
      '维护 AI 模型、凭证与调用审计；客户可见 AI 内容须经多方审核。',
    icon: Bot,
    visible: canSeeAiModelCenterMenu,
  },
  {
    to: '/sms',
    label: '短信模板与日志',
    description:
      '短信模板维护、发送日志查询；展示号码掩码，不展示验证码原文。',
    icon: MessageSquare,
    visible: canSeeSmsAdminMenu,
  },
];

interface MissingSettingSpec {
  readonly key: string;
  readonly label: string;
  readonly reason: string;
}

const MISSING_SETTINGS: readonly MissingSettingSpec[] = [];

interface PermissionGatedMissing extends MissingSettingSpec {
  readonly hidden: (permissions: readonly string[]) => boolean;
}

const PERMISSION_GATED_MISSING: readonly PermissionGatedMissing[] = [
  {
    key: 'audit_log_search',
    label: '审计日志查询',
    reason:
      '当前账号未获得 audit:read / *:* 权限；如需访问管理后台审计日志查询，请联系超级管理员授权。',
    hidden: canReadAuditLogs,
  },
  {
    key: 'wechat_binding',
    label: '微信登录与绑定管理',
    reason:
      '当前账号未获得任一 wechat_* 权限；如需访问微信登录与绑定管理，请联系超级管理员授权。',
    hidden: canReadWechatBindingManagement,
  },
  {
    key: 'mfa_face_settings',
    label: '增强验证（短信/人脸）设置',
    reason:
      '当前账号未获得任一 verification_* 权限；如需查看增强验证策略中心，请联系超级管理员授权。',
    hidden: canReadEnhancedVerification,
  },
  {
    key: 'session_idle_policy',
    label: '会话空闲超时策略',
    reason:
      '当前账号未获得 session_policy:read / *:* 权限；如需查看会话空闲超时策略，请联系超级管理员授权。',
    hidden: canReadSessionPolicy,
  },
];

/**
 * Each settings module that previously lived on the long page is wrapped here
 * as a tab. A tab is **mounted only when it becomes the active tab**, so a
 * section's hooks/API calls do not fire while the user is on another tab and
 * — critically — never fire at all when the caller does not have permission
 * (the tab is excluded from {@link selectVisibleTabs} before any render).
 */
interface SettingsTabSpec {
  readonly key: string;
  readonly label: string;
  readonly visible: (permissions: readonly string[]) => boolean;
  readonly render: (args: SettingsTabRenderArgs) => ReactElement;
}

interface SettingsTabRenderArgs {
  readonly permissions: readonly string[];
  readonly currentUserId: string | null;
}

// RBAC catalog & system-parameter catalog are exposed to all authenticated
// admins (the backend RBAC matrix endpoint and parameter catalog return
// non-sensitive metadata). The tabs are therefore always visible, matching the
// pre-#323 behaviour where the matching sections were unconditionally mounted
// on the long settings page.
function alwaysVisibleTab(): boolean {
  return true;
}

const SETTINGS_TABS: readonly SettingsTabSpec[] = [
  {
    key: 'rbac',
    label: '角色与权限',
    visible: alwaysVisibleTab,
    render: ({ permissions, currentUserId }) => (
      <SettingsRbacSection
        currentUserId={currentUserId}
        currentPermissions={permissions}
      />
    ),
  },
  {
    key: 'tenant_organization',
    label: '租户与机构',
    visible: canSeeTenantOrganizationSection,
    render: ({ permissions }) => (
      <TenantOrganizationSection permissions={permissions} />
    ),
  },
  {
    key: 'system_parameters',
    label: '参数中心',
    visible: alwaysVisibleTab,
    render: ({ permissions }) => (
      <SystemParametersSection permissions={permissions} />
    ),
  },
  {
    key: 'session_policy',
    label: '会话空闲策略',
    visible: canReadSessionPolicy,
    render: ({ permissions }) => (
      <SessionIdlePolicySection permissions={permissions} />
    ),
  },
  {
    key: 'wechat_binding',
    label: '微信绑定',
    visible: canReadWechatBindingManagement,
    render: ({ permissions }) => (
      <WechatBindingManagementSection permissions={permissions} />
    ),
  },
  {
    key: 'enhanced_verification',
    label: '增强验证',
    visible: canReadEnhancedVerification,
    render: ({ permissions }) => (
      <EnhancedVerificationPolicyCenterSection permissions={permissions} />
    ),
  },
  {
    key: 'audit_log',
    label: '审计日志',
    visible: canReadAuditLogs,
    render: ({ permissions, currentUserId }) => (
      <AuditLogSearchSection
        permissions={permissions}
        currentUserId={currentUserId}
      />
    ),
  },
];

function selectVisibleTabs(
  permissions: readonly string[],
): readonly SettingsTabSpec[] {
  return SETTINGS_TABS.filter((tab) => tab.visible(permissions));
}

function maskTenantId(tenantId: string | null): string {
  if (!tenantId) return '—';
  if (tenantId.length <= 8) return tenantId;
  return `${tenantId.slice(0, 4)}…${tenantId.slice(-4)}`;
}

function formatIssuedAt(iso: string | undefined): string {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '—';
  return new Date(ts).toLocaleString();
}

export default function SettingsPage(): ReactElement {
  const { session, permissions, roles, signOut } = useAuth();
  const [showPermissions, setShowPermissions] = useState(false);

  const visibleLinks = useMemo(
    () => SETTINGS_LINKS.filter((l) => l.visible(permissions)),
    [permissions],
  );

  const visibleGatedMissing = useMemo(
    () => PERMISSION_GATED_MISSING.filter((item) => !item.hidden(permissions)),
    [permissions],
  );
  const showMissingSection =
    MISSING_SETTINGS.length > 0 || visibleGatedMissing.length > 0;

  const visibleTabs = useMemo(
    () => selectVisibleTabs(permissions),
    [permissions],
  );
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);

  // Active tab resolution: explicit user choice if still visible, otherwise
  // fall back to the first visible tab. Computing this every render means a
  // permission change (e.g. role rebind during the same session) immediately
  // re-snaps to a tab the user is still allowed to see.
  const resolvedActiveTabKey =
    activeTabKey &&
    visibleTabs.some((tab) => tab.key === activeTabKey)
      ? activeTabKey
      : (visibleTabs[0]?.key ?? null);

  const activeTab = visibleTabs.find((tab) => tab.key === resolvedActiveTabKey);

  const currentUserId = session?.userId ?? null;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <header>
        <h2 className="text-2xl font-semibold">系统设置</h2>
        <p className="text-sm text-muted-foreground">
          本页面只展示当前会话的真实身份/权限信息、已落地的设置类模块入口，以及明确标注的
          "待 Issue 落地"占位。不展示 token、cookie、生产环境变量或任何凭据。
        </p>
      </header>

      <section className="space-y-3" data-testid="settings-session-section">
        <h3 className="text-lg font-medium">当前会话</h3>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UserCog className="h-4 w-4" aria-hidden />
              身份信息
            </CardTitle>
            <CardDescription>
              来自 `/api/v1/auth/me`；展示主体邮箱、用户 ID、租户 ID 掩码、签发时间、来源。
            </CardDescription>
          </CardHeader>
          <CardContent>
            {session ? (
              <dl
                className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-2"
                data-testid="settings-session-grid"
              >
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">主体</dt>
                  <dd data-testid="settings-session-subject">{session.subject}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">用户 ID</dt>
                  <dd data-testid="settings-session-userid">{session.userId}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">租户 ID（掩码）</dt>
                  <dd data-testid="settings-session-tenant">{maskTenantId(session.tenantId)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">签发时间</dt>
                  <dd data-testid="settings-session-issued">{formatIssuedAt(session.issuedAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">来源</dt>
                  <dd data-testid="settings-session-source">
                    {session.source === 'dev-mock' ? 'DEV MOCK（仅本地开发）' : '后端 api-client'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">已授权模块数</dt>
                  <dd data-testid="settings-session-modules">{roles.length}</dd>
                </div>
              </dl>
            ) : (
              <div
                className="rounded border border-dashed py-6 text-center text-sm text-muted-foreground"
                data-testid="settings-session-empty"
              >
                未读取到有效会话；请重新登录。
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4" aria-hidden />
              已授予的权限
            </CardTitle>
            <CardDescription>
              权限矩阵由后端下发，前端只读展示。默认折叠，避免无意义地铺开。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm">
                共 <span data-testid="settings-permissions-count">{permissions.length}</span> 项权限
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPermissions((v) => !v)}
                disabled={permissions.length === 0}
                data-testid="settings-permissions-toggle"
              >
                {showPermissions ? '隐藏' : '展开'}
              </Button>
            </div>
            {showPermissions && permissions.length > 0 && (
              <ul
                className="grid gap-1 text-xs md:grid-cols-2 lg:grid-cols-3"
                data-testid="settings-permissions-list"
              >
                {permissions.map((p) => (
                  <li
                    key={p}
                    className="rounded border bg-muted/40 px-2 py-1 font-mono"
                  >
                    {p}
                  </li>
                ))}
              </ul>
            )}
            {permissions.length === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="settings-permissions-empty">
                当前账号未获得任何业务权限；请联系超级管理员分配。
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={signOut}
            data-testid="settings-signout"
            className="gap-2"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            退出登录
          </Button>
        </div>
      </section>

      <section className="space-y-3" data-testid="settings-modules-section">
        <div>
          <h3 className="text-lg font-medium">已落地的设置类模块</h3>
          <p className="text-sm text-muted-foreground">
            根据当前账号的权限显示。无权限模块不会出现在下方列表中。
          </p>
        </div>
        {visibleLinks.length === 0 ? (
          <Card>
            <CardContent>
              <div
                className="rounded border border-dashed py-8 text-center text-sm text-muted-foreground"
                data-testid="settings-modules-empty"
              >
                当前账号未授予任何已落地的设置类模块权限。
              </div>
            </CardContent>
          </Card>
        ) : (
          <div
            className="grid gap-3 md:grid-cols-2"
            data-testid="settings-modules-grid"
          >
            {visibleLinks.map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.to}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Icon className="h-4 w-4" aria-hidden />
                      {item.label}
                    </CardTitle>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link
                      href={item.to}
                      className="text-sm text-primary underline-offset-2 hover:underline"
                      data-testid={`settings-module-link-${item.to}`}
                    >
                      进入模块
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {visibleTabs.length > 0 && (
        <section className="space-y-3" data-testid="settings-tabs-section">
          <div>
            <h3 className="text-lg font-medium">设置模块</h3>
            <p className="text-sm text-muted-foreground">
              选择需要管理的模块。无权限模块不会出现在选项卡中；隐藏选项卡的接口请求不会触发。
            </p>
          </div>
          <div
            role="tablist"
            aria-label="设置模块"
            data-testid="settings-tabs"
            className="flex flex-wrap gap-2 border-b pb-2"
          >
            {visibleTabs.map((tab) => {
              const isActive = tab.key === resolvedActiveTabKey;
              return (
                <button
                  key={tab.key}
                  role="tab"
                  type="button"
                  aria-selected={isActive}
                  aria-controls={`settings-tabpanel-${tab.key}`}
                  id={`settings-tab-${tab.key}`}
                  data-testid={`settings-tab-${tab.key}`}
                  data-active={isActive ? 'true' : 'false'}
                  className={
                    isActive
                      ? 'rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground'
                      : 'rounded-md border border-transparent bg-muted/60 px-3 py-1.5 text-sm text-muted-foreground hover:border-border hover:bg-muted'
                  }
                  onClick={() => setActiveTabKey(tab.key)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          {activeTab ? (
            <div
              role="tabpanel"
              id={`settings-tabpanel-${activeTab.key}`}
              aria-labelledby={`settings-tab-${activeTab.key}`}
              data-testid={`settings-tabpanel-${activeTab.key}`}
            >
              {activeTab.render({ permissions, currentUserId })}
            </div>
          ) : null}
        </section>
      )}

      {showMissingSection && (
        <section className="space-y-3" data-testid="settings-missing-section">
          <div>
            <h3 className="text-lg font-medium">待 Issue 落地的设置项</h3>
            <p className="text-sm text-muted-foreground">
              以下设置当前版本尚未提供后端契约或前端 Issue。按金专 AI GUIDE 规定，禁止在此处
              编写假设置项或假审批流；落地由各专项 Issue 单独承接。
            </p>
          </div>
          <Card>
            <CardContent className="space-y-2 py-4">
              {MISSING_SETTINGS.map((item) => (
                <div
                  key={item.key}
                  className="rounded border border-dashed p-3 text-sm"
                  data-testid={`settings-missing-${item.key}`}
                >
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs text-muted-foreground">{item.reason}</div>
                </div>
              ))}
              {visibleGatedMissing.map((item) => (
                <div
                  key={item.key}
                  className="rounded border border-dashed p-3 text-sm"
                  data-testid={`settings-missing-${item.key}`}
                >
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs text-muted-foreground">{item.reason}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
